import { v, type Infer } from "convex/values";
import type { ComparisonKind } from "./scheduleComparisonCreation";
import type { projectDocumentPageTypeValidator } from "./projectDocumentPages";

/** Types the classifier or a user can assign to a whole revision. */
export const projectDocumentTypeValidator = v.union(
  v.literal("schedule"),
  v.literal("plan_set"),
  v.literal("generic_pdf"),
  v.literal("other")
);
export type ProjectDocumentType = Infer<typeof projectDocumentTypeValidator>;

/** Document types a page can contribute to its revision. */
export const pageDocumentTypeValidator = v.union(
  v.literal("schedule"),
  v.literal("plan_set"),
  v.literal("generic_pdf")
);
export type PageDocumentType = Infer<typeof pageDocumentTypeValidator>;

/** `mixed` is only derived from page types; it cannot be assigned directly. */
export const effectiveDocumentTypeValidator = v.union(
  projectDocumentTypeValidator,
  v.literal("mixed")
);
export type EffectiveDocumentType = Infer<typeof effectiveDocumentTypeValidator>;

type PageType = Infer<typeof projectDocumentPageTypeValidator>;

/**
 * Page type → document type. Supporting sheets (cover, notes) belong to a plan
 * set but only decide the type when nothing else does, so a schedule book with
 * a cover sheet stays a schedule. `other`/`unknown` pages carry no signal.
 */
const DOCUMENT_TYPE_BY_PAGE_TYPE: Record<PageType, PageDocumentType | "supporting" | null> = {
  plan: "plan_set",
  detail: "plan_set",
  elevation: "plan_set",
  section: "plan_set",
  legend: "plan_set",
  schedule: "schedule",
  spec: "generic_pdf",
  cover: "supporting",
  notes: "supporting",
  other: null,
  unknown: null,
};

/**
 * Whether a page can be part of a `kind` job source: schedule pages for
 * schedules; plan-family and supporting sheets for plan sets.
 */
export function pageFitsDocumentType(pageType: PageType, kind: "schedule" | "plan_set") {
  const mapped = DOCUMENT_TYPE_BY_PAGE_TYPE[pageType];
  return mapped === kind || (kind === "plan_set" && mapped === "supporting");
}

const PAGE_DOCUMENT_TYPE_ORDER: PageDocumentType[] = ["plan_set", "schedule", "generic_pdf"];

/**
 * Derives a revision type from its page types: one document type → that type,
 * several → `mixed`. Returns undefined when no page carries a signal, leaving
 * the first-pages classifier result in place.
 */
export function deriveDocumentTypeFromPages(pageTypes: Iterable<PageType>):
  | { documentType: EffectiveDocumentType; pageDocumentTypes: PageDocumentType[] }
  | undefined {
  const found = new Set<PageDocumentType>();
  let hasSupportingPages = false;
  for (const pageType of pageTypes) {
    const mapped = DOCUMENT_TYPE_BY_PAGE_TYPE[pageType];
    if (mapped === "supporting") hasSupportingPages = true;
    else if (mapped) found.add(mapped);
  }
  if (found.size === 0 && hasSupportingPages) found.add("plan_set");
  if (found.size === 0) return undefined;

  const pageDocumentTypes = PAGE_DOCUMENT_TYPE_ORDER.filter((type) => found.has(type));
  return {
    documentType: pageDocumentTypes.length === 1 ? pageDocumentTypes[0] : "mixed",
    pageDocumentTypes,
  };
}

/**
 * Whether a revision should be processed as `kind`: either it is that type, or
 * it is `mixed` and has pages of that type.
 */
export function revisionHasDocumentType(
  revision: { effectiveDocumentType?: string; pageDocumentTypes?: readonly string[] },
  kind: ProjectDocumentType
) {
  return (
    revision.effectiveDocumentType === kind ||
    (revision.effectiveDocumentType === "mixed" &&
      (revision.pageDocumentTypes?.includes(kind) ?? false))
  );
}

/**
 * RevPilot comparison for a classified revision: schedules get table-level
 * schedule comparison; plan sets and mixed revisions with plan pages get the
 * page-by-page plan-set diff.
 */
export function getRevPilotComparisonKind(
  revision: Parameters<typeof revisionHasDocumentType>[0] & { classificationStatus?: string }
): ComparisonKind | undefined {
  if (revision.classificationStatus !== "completed") return undefined;
  if (revision.effectiveDocumentType === "schedule") return "schedule";
  if (revisionHasDocumentType(revision, "plan_set")) return "plan_set";
  return undefined;
}
